import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, Sparkles, AlertTriangle, CheckCircle2, RefreshCw, Zap, Radio, Calculator } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Link, useNavigate } from "react-router-dom";

/**
 * /admin/qf-qualification — Automatic Quarterfinal qualification & draw.
 *
 * Spec (frozen — do NOT alter):
 *   Setup:   3 groups A/B/C × 4 teams = 12 teams.
 *   Seeds:   #1..#3 = group winners (A,B,C).
 *            #4..#6 = group runners-up (C,B,A).  Positions fixed by finish.
 *            #7..#8 = best 2 by Avg Game Diff (GD / Ties Played) from remaining 6.
 *   Draw:    QF1 #1v#8 · QF2 #4v#5 · QF3 #2v#7 · QF4 #3v#6.
 */

const SEED_TINT = {
  1: "from-[#F4B400]/30 to-[#F4B400]/5 border-[#F4B400]/60",
  2: "from-[#F4B400]/30 to-[#F4B400]/5 border-[#F4B400]/60",
  3: "from-[#F4B400]/30 to-[#F4B400]/5 border-[#F4B400]/60",
  4: "from-[#00A86B]/30 to-[#00A86B]/5 border-[#00A86B]/60",
  5: "from-[#00A86B]/30 to-[#00A86B]/5 border-[#00A86B]/60",
  6: "from-[#00A86B]/30 to-[#00A86B]/5 border-[#00A86B]/60",
  7: "from-[#E32424]/30 to-[#E32424]/5 border-[#E32424]/60",
  8: "from-[#E32424]/30 to-[#E32424]/5 border-[#E32424]/60",
};

export default function QFQualification() {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [existingQF, setExistingQF] = useState([]);
  const [autoSchedule, setAutoSchedule] = useState(true);
  const [revealAt, setRevealAt] = useState("");
  const [showWildcardReport, setShowWildcardReport] = useState(true);
  const [savingReveal, setSavingReveal] = useState(false);
  const [kickoffHour, setKickoffHour] = useState(17);
  const [kickoffMinute, setKickoffMinute] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: matches }, { data: revealCfg }] = await Promise.all([
        api.get("/qf/qualification-preview"),
        api.get("/matches").catch(() => ({ data: [] })),
        api.get("/public/qf/reveal").catch(() => ({ data: {} })),
      ]);
      setPreview(data);
      setExistingQF((matches || []).filter((m) => m.stage === "quarter"));
      if (revealCfg?.reveal_at) setRevealAt(revealCfg.reveal_at.slice(0, 16));
      // wildcard report visibility is stored on tenant config (default true)
      try {
        const { data: cfg } = await api.get("/config");
        if (cfg?.qf_show_wildcard_report !== undefined) setShowWildcardReport(!!cfg.qf_show_wildcard_report);
        if (cfg?.qf_kickoff_hour !== undefined) setKickoffHour(cfg.qf_kickoff_hour);
        if (cfg?.qf_kickoff_minute !== undefined) setKickoffMinute(cfg.qf_kickoff_minute);
      } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to compute qualification");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveRevealConfig = async () => {
    setSavingReveal(true);
    try {
      const payload = {
        reveal_at: revealAt ? new Date(revealAt).toISOString() : "",
        show_wildcard_report: showWildcardReport,
      };
      await api.post("/qf/reveal-config", payload);
      toast.success("Reveal settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingReveal(false);
    }
  };

  const saveTimezone = async () => {
    try {
      await api.put("/config", { qf_kickoff_hour: kickoffHour, qf_kickoff_minute: kickoffMinute });
      toast.success(`Kickoff anchor saved: ${String(kickoffHour).padStart(2, "0")}:${String(kickoffMinute).padStart(2, "0")} UTC`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const publish = async (replace = false) => {
    setPublishing(true);
    try {
      const { data } = await api.post("/qf/publish", { replace_existing: replace, auto_schedule: autoSchedule });
      const scheduleNote = data.auto_scheduled ? " · auto-scheduled" : "";
      toast.success(`Published ${data.created} fixture${data.created !== 1 ? "s" : ""} (QFs + SF/Final placeholders)${scheduleNote}`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Publish failed";
      if (e?.response?.status === 409 && !replace) {
        // Offer replace flow via a second toast action.
        toast.error(detail, {
          action: { label: "Replace", onClick: () => publish(true) },
          duration: 8000,
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setPublishing(false);
    }
  };

  if (loading) return <div className="p-8 text-zinc-400">Loading qualification…</div>;
  if (!preview) return <div className="p-8 text-red-300">Failed to load preview.</div>;

  return (
    <div className="p-6 md:p-8 min-h-screen bg-[#060d09] text-white" data-testid="qf-qualification-page">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-5 h-5 text-[#F4B400]" />
            <h1 className="font-anton text-4xl uppercase tracking-tight">Quarterfinal Qualification</h1>
          </div>
          <p className="text-sm text-zinc-400 max-w-3xl">
            Automatic seeding from the group stage per Mara Padel League Season 03 spec: top 2 of each group (fixed by finish, no re-ranking) plus 2 Wild Cards ranked by Average Game Difference. Matchups: QF1 · #1 v #8, QF2 · #4 v #5, QF3 · #2 v #7, QF4 · #3 v #6.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" className="border-white/10 text-zinc-300" data-testid="qf-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* ─── Readiness banner ───────────────────────────────────── */}
      {!preview.ready && (
        <Card className="p-4 mb-6 bg-amber-500/10 border-amber-500/40 flex items-start gap-3" data-testid="qf-not-ready-banner">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold uppercase tracking-widest text-amber-300 text-xs">Not yet ready to publish</div>
            <div className="text-sm text-zinc-300 mt-1">{preview.ready_reason}</div>
            <div className="text-[11px] text-zinc-500 mt-1">Complete more group-stage matches, or finish provisioning the tournament to 12 teams (4 per group), then refresh.</div>
          </div>
        </Card>
      )}

      {preview.ready && existingQF.length > 0 && (
        <Card className="p-4 mb-6 bg-emerald-500/10 border-emerald-500/40 flex items-start gap-3" data-testid="qf-already-published-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold uppercase tracking-widest text-emerald-300 text-xs">Quarterfinals already published</div>
            <div className="text-sm text-zinc-300 mt-1">{existingQF.length} QF match{existingQF.length !== 1 ? "es are" : " is"} live in the schedule. Republishing replaces every scheduled QF (live/finished ones stay locked).</div>
          </div>
        </Card>
      )}

      {/* ─── Group Tables ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="qf-group-tables">
        {["A", "B", "C"].map((g) => (
          <Card key={g} className="p-4 bg-[#0f1a15] border-white/10">
            <div className="flex items-center justify-between mb-3">
              <div className="font-anton text-2xl uppercase tracking-tight text-[#F4B400]">Group {g}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{(preview.group_tables[g] || []).length} teams</div>
            </div>
            <div className="space-y-1.5">
              {(preview.group_tables[g] || []).map((row) => {
                const isTop2 = row.position <= 2;
                return (
                  <div
                    key={row.team_id}
                    className={`flex items-center gap-2 p-2 rounded-lg border ${
                      isTop2 ? "bg-[#F4B400]/10 border-[#F4B400]/40" : "bg-black/30 border-white/5"
                    }`}
                    data-testid={`qf-group-${g}-row-${row.position}`}
                  >
                    <div className={`w-6 text-center text-xs font-bold ${isTop2 ? "text-[#F4B400]" : "text-zinc-500"}`}>{row.position}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{row.team_name}</div>
                      <div className="text-[10px] text-zinc-500">
                        W{row.won}-L{row.lost} · GW {row.games_won} · GL {row.games_lost}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-bold ${row.game_diff > 0 ? "text-emerald-400" : row.game_diff < 0 ? "text-red-400" : "text-zinc-400"}`}>
                        {row.game_diff > 0 ? "+" : ""}{row.game_diff}
                      </div>
                      <div className="text-[9px] text-zinc-500">AGD {row.avg_game_diff.toFixed(2)}</div>
                    </div>
                  </div>
                );
              })}
              {(preview.group_tables[g] || []).length === 0 && (
                <div className="text-xs text-zinc-500 italic text-center py-4">No teams assigned to Group {g}</div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* ─── Seed Ladder ────────────────────────────────────────── */}
      <div className="mb-6">
        <div className="font-anton text-2xl uppercase tracking-tight mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#F4B400]" /> Qualified Teams · Seeds #1 → #8
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="qf-seed-ladder">
          {preview.seeds.map((s) => (
            <Card
              key={s ? s.seed : Math.random()}
              className={`p-4 bg-gradient-to-br ${SEED_TINT[s?.seed] || "from-white/5 to-white/0 border-white/10"} border`}
              data-testid={`qf-seed-${s?.seed || "unknown"}`}
            >
              <div className="flex items-baseline justify-between mb-1">
                <div className="font-anton text-3xl leading-none">#{s?.seed}</div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-400">{s?.source}</div>
              </div>
              {s ? (
                <>
                  <div className="text-sm font-bold text-white truncate">{s.team_name}</div>
                  <div className="text-[10px] text-zinc-400 mt-1">
                    Grp {s.group} · GD {s.game_diff > 0 ? "+" : ""}{s.game_diff} · AGD {s.avg_game_diff.toFixed(2)}
                  </div>
                </>
              ) : (
                <div className="text-xs text-zinc-500 italic">Not yet determined</div>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* ─── QF Matchups ────────────────────────────────────────── */}
      <div className="mb-6">
        <div className="font-anton text-2xl uppercase tracking-tight mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#E32424]" /> Auto-Generated Quarterfinal Draw
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="qf-matchups">
          {preview.matchups.map((m) => (
            <Card key={m.label} className="p-4 bg-[#0f1a15] border-white/10" data-testid={`qf-matchup-${m.label}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#F4B400] font-bold mb-3">{m.label}</div>
              <SeedRow seed={m.seed_a} team={m.team_a} />
              <div className="text-center text-[10px] uppercase tracking-widest text-zinc-500 my-1.5">vs</div>
              <SeedRow seed={m.seed_b} team={m.team_b} />
            </Card>
          ))}
        </div>
      </div>

      {/* ─── Admin controls: auto-schedule + reveal config ────── */}
      <Card className="mb-6 p-5 bg-[#0f1a15] border-white/10" data-testid="qf-admin-controls">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-[#00A86B]" />
              <h2 className="font-anton text-lg uppercase tracking-tight">Auto-Slot Fixtures</h2>
            </div>
            <label className="flex items-center gap-3 text-xs text-zinc-300 mb-2">
              <Switch checked={autoSchedule} onCheckedChange={setAutoSchedule} data-testid="qf-auto-schedule-toggle" />
              Auto-schedule QFs + SF/Final into the next free evening slots on Court 1 & 2
            </label>
            <p className="text-[11px] text-zinc-500 mb-3">
              QF1+QF2 land in one slot, QF3+QF4 in the next. SFs land the next day; the Final follows. You can still edit any date/court/team afterwards via <Link to="/admin/knockouts" className="underline text-[#F4B400]">KO Bracket</Link>.
            </p>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Kickoff Anchor (UTC)</label>
            <div className="mt-1 flex items-center gap-2">
              <Input type="number" min={0} max={23} value={kickoffHour}
                onChange={(e) => setKickoffHour(Math.max(0, Math.min(23, parseInt(e.target.value) || 0)))}
                className="bg-black/40 border-white/10 text-white text-xs w-20"
                data-testid="qf-kickoff-hour" />
              <span className="text-zinc-500 text-xs">:</span>
              <Input type="number" min={0} max={59} value={kickoffMinute}
                onChange={(e) => setKickoffMinute(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                className="bg-black/40 border-white/10 text-white text-xs w-20"
                data-testid="qf-kickoff-minute" />
              <Button onClick={saveTimezone} size="sm"
                className="bg-[#00A86B] text-black font-bold uppercase tracking-widest hover:bg-[#00c17b]"
                data-testid="qf-save-timezone-btn">Save</Button>
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">Leagues outside EAT can pick their own hour — default 17:30 UTC.</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Radio className="w-4 h-4 text-[#F4B400]" />
              <h2 className="font-anton text-lg uppercase tracking-tight">Public Draw Reveal</h2>
            </div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Reveal target time (blank = immediate)</label>
            <Input
              type="datetime-local"
              value={revealAt}
              onChange={(e) => setRevealAt(e.target.value)}
              className="mt-1 bg-black/40 border-white/10 text-white text-xs"
              data-testid="qf-reveal-datetime"
            />
            <label className="mt-3 flex items-center gap-3 text-xs text-zinc-300">
              <Switch checked={showWildcardReport} onCheckedChange={setShowWildcardReport} data-testid="qf-wildcard-report-toggle" />
              Show Wild-Card explainer on public standings
            </label>
            <div className="mt-3 flex items-center gap-2">
              <Button onClick={saveRevealConfig} disabled={savingReveal} size="sm"
                className="bg-[#00A86B] text-black font-bold uppercase tracking-widest hover:bg-[#00c17b]"
                data-testid="qf-save-reveal-btn">
                {savingReveal ? "Saving…" : "Save"}
              </Button>
              <a href="/qf-reveal" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded border border-[#F4B400]/40 text-[#F4B400] hover:bg-[#F4B400]/10"
                data-testid="qf-open-reveal-link">
                Open reveal page →
              </a>
            </div>
          </div>
        </div>
      </Card>

      {/* ─── Confirmation CTA ───────────────────────────────────── */}
      <div className="sticky bottom-4 flex justify-end pointer-events-none">
        <div className="bg-[#0f1a15]/95 backdrop-blur border border-white/10 rounded-xl px-4 py-3 shadow-2xl flex items-center gap-3 pointer-events-auto max-w-[640px]">
          <div className="text-[11px] text-zinc-400 max-w-[240px] hidden md:block">
            Once published, the 4 fixtures appear in Schedule and become editable via KO Bracket.
          </div>
          <Button
            onClick={() => publish(existingQF.length > 0)}
            disabled={!preview.ready || publishing}
            className="bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00] disabled:opacity-40 whitespace-nowrap"
            data-testid="qf-confirm-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            {publishing ? "Publishing…" : existingQF.length > 0 ? "Confirm & Republish Draw" : "Confirm Quarterfinal Draw"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SeedRow({ seed, team }) {
  if (!team) {
    return (
      <div className="flex items-center gap-2 p-2 rounded-lg border border-dashed border-white/10 bg-black/20 min-h-[48px] text-zinc-500 italic text-xs">
        Seed #{seed} · to be determined
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg border ${seed <= 3 ? "border-[#F4B400]/40 bg-[#F4B400]/5" : seed <= 6 ? "border-[#00A86B]/40 bg-[#00A86B]/5" : "border-[#E32424]/40 bg-[#E32424]/5"}`}>
      <div className={`w-8 text-center font-anton text-lg ${seed <= 3 ? "text-[#F4B400]" : seed <= 6 ? "text-[#00A86B]" : "text-[#E32424]"}`}>#{seed}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate font-bold">{team.team_name}</div>
        <div className="text-[10px] text-zinc-500">Grp {team.group} · GD {team.game_diff > 0 ? "+" : ""}{team.game_diff}</div>
      </div>
    </div>
  );
}
